import React, { useState, useEffect, useRef } from 'react';
import './PropertyMiniMap.css';

// Southern Cyprus free-area bounding box
const SOUTH_CY_BOUNDS = [[34.35, 32.0], [35.22, 34.65]];

// Neighbourhood-level fallback coordinates (southern Cyprus only)
const COORDS = {
  // Cities
  Limassol:      [34.6841, 33.0392],
  Nicosia:       [35.1563, 33.3896],
  Paphos:        [34.7754, 32.4219],
  Larnaca:       [34.9003, 33.6233],
  Famagusta:     [35.0380, 33.9830], // district → Paralimni (free south)
  Ammochostos:   [35.0380, 33.9830],
  'Ayia Napa':   [34.9843, 34.0000],
  'Agia Napa':   [34.9843, 34.0000],
  Protaras:      [35.0122, 34.0557],
  Paralimni:     [35.0380, 33.9830],
  Polis:         [35.0350, 32.4250],
  // Limassol
  Germasogeia:   [34.6823, 33.0823],
  'Mesa Geitonia': [34.6750, 33.0550],
  'Agios Tychon': [34.6650, 33.0700],
  Polemidia:     [34.6950, 33.0200],
  Neapolis:      [34.6780, 33.0450],
  Marina:        [34.6700, 33.0480],
  Zakaki:        [34.6900, 33.0100],
  Ypsonas:       [34.7050, 32.9900],
  Pyrgos:        [34.6750, 33.0150],
  Columbia:      [34.6810, 33.0600],
  Parekklisia:   [34.6470, 33.1000],
  Mouttagiaka:   [34.6760, 33.0950],
  // Nicosia
  Strovolos:     [35.1280, 33.3620],
  Engomi:        [35.1680, 33.3320],
  Latsia:        [35.1050, 33.3500],
  Geri:          [35.1400, 33.4500],
  Aglandjia:     [35.1600, 33.4000],
  Aglantzia:     [35.1600, 33.4000],
  Anthoupoli:    [35.1750, 33.3200],
  Lakatamia:     [35.1200, 33.3250],
  Tseri:         [35.1600, 33.4200],
  Dali:          [35.0050, 33.4250],
  // Paphos
  'Kato Paphos': [34.7580, 32.4100],
  'Kato Pafos':  [34.7580, 32.4100],
  Chloraka:      [34.7950, 32.4050],
  Peyia:         [34.8384, 32.3500],
  Pegia:         [34.8384, 32.3500],
  'Coral Bay':   [34.8384, 32.3944],
  'Mesa Chorio': [34.8200, 32.4200],
  Tala:          [34.8100, 32.4000],
  Kissonerga:    [34.8050, 32.3850],
  Emba:          [34.8250, 32.4100],
  Latchi:        [35.0500, 32.3950],
  // Larnaca
  Mackenzie:     [34.8975, 33.6367],
  Mckenzie:      [34.8975, 33.6367],
  Finikoudes:    [34.9150, 33.6350],
  Aradippou:     [34.9550, 33.5900],
  Aradipou:      [34.9550, 33.5900],
  Livadia:       [34.9300, 33.6100],
  Oroklini:      [34.9600, 33.6600],
  Pervolia:      [34.8650, 33.5950],
  Dromolaxia:    [34.9350, 33.5650],
  // Famagusta district (free south)
  Deryneia:      [35.0500, 33.9600],
  Sotira:        [35.0650, 33.9350],
  Xylofagou:     [34.9800, 33.8500],
  Liopetri:      [34.9900, 33.9300],
};

function resolveCoords(region, city) {
  const keys = Object.keys(COORDS);
  const match = (val) => {
    if (!val) return null;
    if (COORDS[val]) return COORDS[val];
    const key = keys.find(k => k.toLowerCase() === val.toLowerCase());
    return key ? COORDS[key] : null;
  };
  // Prefer city (more specific) over region/district to avoid district capitals
  // pinning e.g. Ayia Napa listings to Paralimni (the Famagusta district fallback).
  return match(city) || match(region);
}

function ensureLeafletCss() {
  if (!document.getElementById('leaflet-css')) {
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
}

export default function PropertyMiniMap({ listing }) {
  const mapRef          = useRef(null);
  const mapInstanceRef  = useRef(null);
  const mountedRef      = useRef(true);
  const [coords, setCoords]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Step 1: resolve coordinates ──────────────────────────────────────────
  useEffect(() => {
    // Use pre-stored lat/lng if the DB has them
    if (listing.lat && listing.lng) {
      setCoords([parseFloat(listing.lat), parseFloat(listing.lng)]);
      setLoading(false);
      return;
    }

    setCoords(null);
    setLoading(true);

    const fallback   = resolveCoords(listing.region, listing.city);
    const parts      = [listing.address, listing.city, listing.region, 'Cyprus'].filter(Boolean);
    const controller = new AbortController();
    const timer      = setTimeout(() => controller.abort(), 6000);

    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(parts.join(', '))}&countrycodes=cy&limit=1`,
      { signal: controller.signal, headers: { 'Accept-Language': 'en', 'User-Agent': 'CosmoEstates/1.0' } }
    )
      .then(r => r.json())
      .then(data => {
        clearTimeout(timer);
        if (!mountedRef.current) return;
        if (data?.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          // Accept only southern Cyprus
          if (lat >= 34.35 && lat <= 35.22 && lon >= 32.0 && lon <= 34.65) {
            setCoords([lat, lon]);
            setLoading(false);
            return;
          }
        }
        setCoords(fallback);
        setLoading(false);
      })
      .catch(() => {
        clearTimeout(timer);
        if (!mountedRef.current) return;
        setCoords(fallback);
        setLoading(false);
      });

    return () => { clearTimeout(timer); controller.abort(); };
  }, [listing.id, listing.lat, listing.lng, listing.address, listing.region, listing.city]);

  // ── Step 2: initialise / reinitialise Leaflet once coords land ───────────
  useEffect(() => {
    if (!coords || !mapRef.current) return;

    ensureLeafletCss();

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const L        = window.L || require('leaflet');
    const hasExact = (listing.lat && listing.lng) || !!listing.address;
    const zoom     = hasExact ? 16 : 14;

    mapInstanceRef.current = L.map(mapRef.current, {
      center:               coords,
      zoom,
      zoomControl:          false,   // we add it manually below so we can position it
      maxBounds:            SOUTH_CY_BOUNDS,
      maxBoundsViscosity:   0.85,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(mapInstanceRef.current);

    L.control.zoom({ position: 'bottomright' }).addTo(mapInstanceRef.current);

    // Teardrop pin in brand gold accent
    const pinSvg = `<svg width="28" height="38" viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 9.8 14 24 14 24s14-14.2 14-24C28 6.27 21.73 0 14 0z" fill="#0E0E10"/>
      <circle cx="14" cy="14" r="6" fill="white"/>
      <circle cx="14" cy="14" r="3" fill="#B8924A"/>
    </svg>`;
    const icon = L.divIcon({
      className: '',
      html:      `<div class="propertyMiniMap__pin">${pinSvg}</div>`,
      iconSize:  [28, 38],
      iconAnchor:[14, 38],
    });
    L.marker(coords, { icon }).addTo(mapInstanceRef.current);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [coords, listing.lat, listing.lng, listing.address]);

  if (!loading && !coords) return null;

  return (
    <div className="propertyMiniMap">
      {loading && (
        <div className="propertyMiniMap__loader">
          <div className="propertyMiniMap__spinner" />
          <span>Locating property…</span>
        </div>
      )}

      <div
        ref={mapRef}
        className="propertyMiniMap__map"
        style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
      />

    </div>
  );
}
